import { useMemo } from 'react';

import { dynamicConfigClient } from '@trello/dynamic-config';
import { useFeatureGate } from '@trello/feature-gate-client';

import { useCardType } from 'app/src/components/CardType';
import { useMirrorCardQuery } from './MirrorCardQuery.generated';
import { useMirrorCardSourceIdFragment } from './MirrorCardSourceIdFragment.generated';
import { useTrelloMirrorCardSourceCardIdFragment } from './TrelloMirrorCardSourceCardIdFragment.generated';

export const useMirrorCardSourceId = (
  cardId: string,
): { mirrorCardSourceId: string | null; mirrorCardError?: string } => {
  const cardType = useCardType(cardId);

  const { value: isMirrorCardsEnabled } = useFeatureGate('xf_mirror_cards');
  const isGhostUseMirrorCardQuickloadEnabled = dynamicConfigClient.get<boolean>(
    'trello_web_mirror_cards_quickload',
  );

  const { data } = useMirrorCardSourceIdFragment({
    from: { id: cardId },
    returnPartialData: true,
  });

  const { data: nativeData } = useTrelloMirrorCardSourceCardIdFragment({
    from: { id: data?.nodeId },
  });

  const {
    data: mirrorData,
    error,
    loading,
  } = useMirrorCardQuery({
    variables: { id: data?.mirrorSourceId ?? '' },
    skip:
      !data?.mirrorSourceId ||
      cardType !== 'mirror' ||
      !!nativeData?.sourceCard?.id,
    waitOn: isGhostUseMirrorCardQuickloadEnabled
      ? ['TrelloBoardMirrorCards']
      : [],
    errorPolicy: 'all',
    context: {
      returnNotFoundError: true,
      returnNetworkErrors: true,
      operationName: 'MirrorCardQuery',
    },
  });

  const sourceCardId = useMemo(() => {
    if (isGhostUseMirrorCardQuickloadEnabled && nativeData?.id) {
      return nativeData.sourceCard?.objectId ?? null;
    }
    return mirrorData?.card?.id ?? null;
  }, [
    isGhostUseMirrorCardQuickloadEnabled,
    mirrorData?.card?.id,
    nativeData?.id,
    nativeData?.sourceCard?.objectId,
  ]);

  if (loading) {
    return { mirrorCardSourceId: null };
  }

  if (!isMirrorCardsEnabled || cardType !== 'mirror') {
    return { mirrorCardSourceId: null };
  }

  if (error || !sourceCardId) {
    const mirrorCardError =
      error?.message === 'unauthorized card permission requested'
        ? 'notPermitted'
        : 'notFound';
    return {
      mirrorCardSourceId: cardId,
      mirrorCardError,
    };
  }

  return { mirrorCardSourceId: sourceCardId };
};
